import {
  type ChangeEvent,
  forwardRef,
  type ForwardedRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "../../../utils/classnames";
import { InputSize, type InputParser, type BaseInputProps } from "./types";

export { InputSize } from "./types";
export type { InputParser, BaseInputProps } from "./types";

const sizeClass = {
  [InputSize.BASE]: "h-12",
  [InputSize.SM]: "h-10"
};

const iconPx = {
  [InputSize.BASE]: 20,
  [InputSize.SM]: 16
};

const plainTextParser: InputParser<string> = {
  inputMode: "text",
  validate: () => true,
  parse: (raw) => raw,
  format: (value) => value ?? "",
  toEditable: (value) => value ?? ""
};

function BaseInputInner<T = string>(
  props: BaseInputProps<T>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const {
    parser,
    value,
    onChange,
    type = "text",
    id,
    name,
    label,
    labelClassName,
    required,
    hideOptional,
    optionalText,
    size = InputSize.BASE,
    placeholder,
    className,
    inputClassName,
    prefix,
    suffix,
    Icon,
    ErrorIcon,
    iconClassName,
    error,
    hideErrorMessage,
    readOnly,
    disabled,
    autoFocus,
    focusOnError,
    scrollIntoViewOnFocus,
    autoComplete,
    maxLength,
    onFocus: _onFocus,
    onBlur: _onBlur,
    onKeyDown,
    onPaste
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [rawValue, setRawValue] = useState("");
  const internalRef = useRef<HTMLInputElement | null>(null);

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      internalRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref)
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [ref]
  );

  useEffect(() => {
    if (error && focusOnError) {
      internalRef.current?.focus();
    }
  }, [error, focusOnError]);

  const effectiveParser = (parser ?? plainTextParser) as InputParser<T>;
  // The discriminated union ensures type safety at the call site.
  // Inside the component, parser presence guarantees T matches.
  const emit = onChange as (value: T) => void;

  const typedValue = value as T;

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setRawValue(effectiveParser.toEditable(typedValue));
    if (scrollIntoViewOnFocus) {
      internalRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
    _onFocus?.();
  }, [effectiveParser, typedValue, scrollIntoViewOnFocus, _onFocus]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;
      const raw = e.target.value;
      if (effectiveParser.validate(raw)) {
        const parsed = effectiveParser.parse(raw);
        const editable = effectiveParser.formatWhileEditing
          ? effectiveParser.toEditable(parsed)
          : raw;
        setRawValue(editable);
        emit(parsed);
      }
    },
    [effectiveParser, emit, readOnly, disabled]
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const cleaned = effectiveParser.cleanup
      ? effectiveParser.cleanup(rawValue)
      : rawValue;
    emit(effectiveParser.parse(cleaned));
    _onBlur?.();
  }, [effectiveParser, rawValue, emit, _onBlur]);

  const displayValue = isFocused
    ? rawValue
    : effectiveParser.format(typedValue);
  const hasError = Boolean(error);
  const errorId = `${id}-error`;
  const hasErrorMessage =
    typeof error === "string" && error && !hideErrorMessage;
  const isInert = readOnly || disabled;

  const labelId = `${id}-label`;

  return (
    <label className={cx("block cursor-text", className)}>
      {label ? (
        <span
          id={labelId}
          className={cx(
            "text-content-700 mb-1 block text-sm font-medium",
            labelClassName
          )}
        >
          {label}
          {!required && !isInert && !hideOptional ? (
            <span className="text-content-100">
              {" "}
              {optionalText || "(optional)"}
            </span>
          ) : null}
        </span>
      ) : null}

      <span
        className={cx(
          "flex items-center bg-white rounded-md border shadow-xs border-alpha-100",
          sizeClass[size],
          hasError ? "border-danger-300" : "",
          disabled ? "bg-alpha-40 opacity-60 cursor-not-allowed" : "",
          readOnly && !disabled ? "bg-alpha-40" : "",
          !isInert
            ? "focus-within:border-primary-100 focus-within:shadow-focus-ring"
            : ""
        )}
      >
        {Icon ? (
          <span
            className={cx(
              "shrink-0 stroke-content-400",
              size === InputSize.BASE ? "pl-3.5" : "pl-2.5"
            )}
          >
            <HugeiconsIcon
              icon={Icon}
              className={cx("shrink-0", iconClassName)}
              size={iconPx[size]}
            />
          </span>
        ) : null}

        {prefix ? (
          <span
            className={cx(
              "shrink-0 select-none text-base",
              Icon ? "pl-1" : "pl-3",
              isInert || displayValue === "" ? "text-content-100" : ""
            )}
          >
            {prefix}
          </span>
        ) : null}

        <input
          className={cx(
            "min-w-0 flex-1 border-0 bg-transparent text-base font-medium outline-none ring-0 focus:outline-none focus:ring-0",
            "placeholder:text-content-100 placeholder:text-base placeholder:font-medium",
            hasError ? "text-danger-700" : "",
            readOnly ? "text-content-100" : "",
            disabled ? "cursor-not-allowed" : "",
            prefix || Icon ? "pl-1.5" : "pl-3",
            suffix || (hasError && ErrorIcon) ? "pr-1" : "pr-3",
            inputClassName
          )}
          id={id}
          name={name || id}
          type={parser ? "text" : type}
          inputMode={parser ? effectiveParser.inputMode : undefined}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          autoFocus={autoFocus}
          ref={setRef}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          autoComplete={autoComplete}
          maxLength={maxLength}
          aria-invalid={hasError}
          aria-labelledby={label ? labelId : undefined}
          aria-describedby={hasErrorMessage ? errorId : undefined}
        />

        {suffix ? (
          <span
            className={cx(
              "shrink-0 select-none text-base text-content-100",
              hasError && ErrorIcon ? "pr-1" : "pr-3"
            )}
          >
            {suffix}
          </span>
        ) : null}

        {hasError && ErrorIcon ? (
          <span className="shrink-0 pr-3 stroke-danger-500 text-danger-500">
            <HugeiconsIcon
              icon={ErrorIcon}
              className="shrink-0"
              size={iconPx[size]}
            />
          </span>
        ) : null}
      </span>

      {hasErrorMessage ? (
        <span
          id={errorId}
          className="mt-1.5 block text-xs text-danger-500"
          role="alert"
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

const BaseInput = forwardRef(BaseInputInner) as <T = string>(
  props: BaseInputProps<T> & { ref?: ForwardedRef<HTMLInputElement> }
) => ReturnType<typeof BaseInputInner>;

export { BaseInput };
